import React, { useEffect, useRef, useState } from "react";
import CooperativeInfo from "./CooperativeInfo";
import Address from "./Address";
import useMobile from "../../hooks/useMobile";
import Financials from "./Financials";
import Committee from "./Committee";
import ShareMember from "./ShareMember";
import Documents from "./Documents";
import Review from "./Review";
import {
  IAddress,
  ICommitteeMemberInput,
  ICooperativeDetails,
  IDeclaration,
  IDocumentsInput,
  IExecutiveMemberInput,
  IFinancialAuditInput,
  IFinancialIncome,
  IFinancialLoan,
  IFinancialShare,
  IFinancialTax,
  IShareMemberInput,
} from "../../exports/interfaces";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import { useNavigate } from "react-router-dom";
import { useLoader } from "../../context/LoaderContext";
import axiosClient from "../../axioxClient.ts/axiosClient";
import Declaration from "./Declaration";
import {
  ScanEye,
  ShieldCheck,
  FileText,
  User,
  UserRound,
  MapPin,
  Banknote,
  CircleCheck,
  List,
  ChevronRight,
} from "lucide-react";
import {
  clearRegisterLocalStorage,
  loadAllRegisterData,
  saveAddressDraft,
  saveAuditFinancialDraft,
  saveCommitteeCooperativeDraft,
  saveCommitteeTableDraft,
  saveCooperativeDraft,
  saveDeclarationDraft,
  saveDocumentsDraft,
  saveExecutiveCooperativeDraft,
  saveExecutiveTableDraft,
  saveIncomeFinancialDraft,
  saveLoanFinancialDraft,
  saveShareFinancialDraft,
  saveShareMemberDraft,
  saveTaxFinancialDraft,
  saveToLocalStorage,
} from "./registerDraft";
import {
  getAddressErrors,
  getAuditFinancialErrors,
  getAuditFinancialTab,
  getCooperativeInfoErrors,
  getDeclarationErrors,
  getDocumentErrors,
  getIncomeFinancialErrors,
  getIncomeFinancialTab,
  getLoanFinancialErrors,
  getLoanFinancialTab,
  getShareFinancialErrors,
  getShareFinancialTab,
  getShareMemberErrors,
  getTaxFinancialErrors,
  getTaxFinancialTab,
} from "./registerValidation";
import Select from "react-select";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import { useOption } from "../../context/OptionContext";
import { useUser } from "../../context/UserContext";
import { useNotification } from "../../context/NotificationContext";

const ignoredKeys = [
  "file",
  "fileName",
  "documentUrl",
  "bodFile",
  "bodFileName",
  "bodDocumentUrl",
  "citizenshipFront",
  "citizenshipFrontName",
  "citizenshipFrontUrl",
  "citizenshipBack",
  "citizenshipBackName",
  "citizenshipBackUrl",
];

const SelectWithError = Select as unknown as React.ComponentType<any>;

const RegisterNew = () => {
  const steps = [
    { label: "Cooperative Info", icon: List },
    { label: "Address", icon: MapPin },
    { label: "Financials", icon: Banknote },
    { label: "Committee & Executive", icon: UserRound },
    { label: "Share Member", icon: User },
    { label: "Documents", icon: FileText },
    { label: "Declaration", icon: ShieldCheck },
    { label: "Review", icon: ScanEye },
  ];
  const { setLoading } = useLoader();

  const { categories } = useOption();

  const { user } = useUser();
  const { sendNotification } = useNotification();

  const [financialsactiveTab, setFinancialsActiveTab] = useState("share");
  const [committeeactiveTab, setCommitteeActiveTab] = useState("committees");

  const navigate = useNavigate();

  const fiscalYearOptions =
    categories
      ?.find((item) => item.name === "Fiscal Year")
      ?.dropdowns?.filter((item) => item.status === "Active")
      ?.sort((a, b) => a.order - b.order)
      ?.map((item) => ({
        value: item.label,
        label: item.label,
      })) || [];

  const [cooperativeInfo, setCooperativeInfo] = useState<ICooperativeDetails>({
    registrationNumber: "",
    businessregistrationNumber: "",
    nameNepali: "",
    nameEnglish: "",
    natureOfWork: "",
    status: "",
    mergedCooperativeName: "",
    mergedDateNepali: "",
    mergedDateEnglish: "",
    mergeReason: "",
    closeDateNepali: "",
    closeDateEnglish: "",
    workingArea: [],
    panNumber: "",
    email: "",
    mobileNumber: "",
    contactPerson: "",
    registeredDate: "",
    registeredEnglishDate: "",
    registeredAt: "",
    registeredOfficeAddress: "",
    isRegisteredInFederation: null,
    womenLead: null,
    staffCount: null,
    softwareUsed: "",
    maleMembers: null,
    femaleMembers: null,
    otherMembers: null,
  });

  const [address, setAddress] = useState<IAddress>({
    province: "",
    district: "",
    municipality: "",
    streetTole: "",
    ward: "",
  });

  const [shareFinancial, setShareFinancial] = useState<IFinancialShare>({
    fiscalYear: "",
    maleMembers: null,
    femaleMembers: null,
    otherMembers: null,
    institutionalMembers: null,
    totalShareMembers: null,
    totalGeneralSaving: "",
    totalRegularSaving: "",
    totalPeriodicSaving: "",
    totalSavingAmount: "",
    femaleDepositors: null,
    maleDepositors: null,
    otherDepositors: null,
    totalDepositors: null,
    balanceSheet: "",
    totalLiabilities: "",
    totalAssets: "",
    totalLiquidAssets: "",
    tlaToTaRatio: "",
    cash: "",
    association: "",
    districtUnion: "",
    bank: "",
  });

  const [loanFinancial, setLoanFinancial] = useState<IFinancialLoan>({
    fiscalYear: "",

    totalShareCapital: "",
    totalReserveFund: "",
    deficitReplenishmentFund: "",
    accumulatedProfitLoss: "",
    coreCapital: "",

    totalPassLoan: "",
    totalSubStandardLoan: "",
    totalDoubtfulLoan: "",
    totalLossLoan: "",
    totalLoanDisbursed: "",
    externalLoan: "",
    maleBorrowers: null,
    femaleBorrowers: null,
    othersBorrowers: null,
    totalBorrowers: null,

    llpPassLoan: "",
    llpSubStandardLoan: "",
    llpDoubtfulLoan: "",
    llpLossLoan: "",

    totalLoanLossProvision: "",
    additionalProvisionLoanLoss: "",

    assetsRevaluationReserve: "",
    freeReserve: "",
    supplementaryCapital: "",
    totalCapitalFund: "",

    totalNonPerformingLoan: "",
    nplPercentage: "",
    totalRiskWeightedAssets: "",

    coreCapitalRatio: "",
    totalCapitalFundRatio: "",

    totalBorrowing: "",

    loanRescheduledCount: null,
    loanRescheduledAmount: "",

    investmentPrivateLtd: "",
    investmentHouseBuilding: "",
  });

  const [incomeFinancial, setIncomeFinancial] = useState<IFinancialIncome>({
    fiscalYear: "",
    operationalIncome: "",
    otherIncome: "",
    operationalExpenses: "",
    otherExpenses: "",
    netProfitLoss: "",
  });

  const [auditFinancial, setAuditFinancial] = useState<IFinancialAuditInput>({
    fiscalYear: "",
    auditorName: "",
    auditorMobile: "",
    auditorAddress: "",
    icanNumber: "",
    agmDate: "",
    agmNepaliDate: "",
    participants: null,
    participantsAttended: null,
    participantsPercent: "",
    file: null,
    fileName: "",
  });

  const [taxFinancial, setTaxFinancial] = useState<IFinancialTax>({
    fiscalYear: "",
    incomeTax: "",
    interestTax: "",
    employeeTax: "",
    taxOthers: "",
    serviceTax: "",
    rentalTax: "",
    localOthers: "",
  });

  const [committeeCooperative, setCommitteeCooperative] =
    useState<ICommitteeMemberInput>({
      fiscalYear: "",
      committeeType: "",
      position: "",
      positionStatus: "",
      grandmotherName: "",
      fatherName: "",
      maritalStatus: "",
      husbandName: "",
      fullName: "",
      gender: "",
      mobile: "",
      email: "",
      address: "",

      citizenship: "",
      pan: "",
      nid: "",
      issuedDistrict: "",
      tenureStart: "",
      tenureEnd: "",

      province: "",
      district: "",
      municipality: "",
      ward: "",
      street: "",

      education: "",
      file: null,
      fileName: "",
      bodFile: null,
      bodFileName: "",
      citizenshipFront: null,
      citizenshipFrontName: "",
      citizenshipBack: null,
      citizenshipBackName: "",
    });

  const [committeeTableCooperative, setCommitteeTableCooperative] = useState<
    ICommitteeMemberInput[]
  >([]);

  const [editCommitteeIndex, setEditCommitteeIndex] = useState<number | null>(
    null,
  );

  const [executiveCooperative, setExecutiveCooperative] =
    useState<IExecutiveMemberInput>({
      fiscalYear: "",
      position: "",
      positionStatus: "",
      grandmotherName: "",
      fatherName: "",
      maritalStatus: "",
      husbandName: "",
      fullName: "",
      gender: "",

      email: "",
      address: "",
      mobile: "",
      pan: "",
      nid: "",
      citizenship: "",
      issuedDistrict: "",

      province: "",
      district: "",
      municipality: "",
      ward: "",
      street: "",

      experience: null,
      currentPositionDuration: null,
      education: "",
      file: null,
      fileName: "",
      citizenshipFront: null,
      citizenshipFrontName: "",
      citizenshipBack: null,
      citizenshipBackName: "",
    });

  const [executiveTableCooperative, setExecutiveTableCooperative] = useState<
    IExecutiveMemberInput[]
  >([]);

  const [editExecutiveIndex, setEditExecutiveIndex] = useState<number | null>(
    null,
  );

  const [shareMember, setShareMember] = useState<IShareMemberInput>({
    shareMemberFile: null,
    shareMemberFileName: "",
    shareMaleMembers: null,
    shareFemaleMembers: null,
    shareOthersMembers: null,
    shareTotalMembers: null,

    savingMemberFile: null,
    savingMemberFileName: "",
    savingMaleMembers: null,
    savingFemaleMembers: null,
    savingOthersMembers: null,
    savingTotalMembers: null,

    loanMemberFile: null,
    loanMemberFileName: "",
    loanMaleMembers: null,
    loanFemaleMembers: null,
    loanOthersMembers: null,
    loanTotalMembers: null,
  });

  const [declaration, setDeclaration] = useState<IDeclaration>({
    name: "",
    position: "",
    mobile: "",
    declaration: "",
  });

  const [documents, setDocuments] = useState<IDocumentsInput>({
    regCertificateFile: null,
    regCertificateFileName: "",

    lawDocumentFile: null,
    lawDocumentFileName: "",

    foundingDocumentFile: null,
    foundingDocumentFileName: "",

    panDocumentFile: null,
    panDocumentFileName: "",

    meetingDocumentFile: null,
    meetingDocumentFileName: "",

    businessRegistrationFile: null,
    businessRegistrationFileName: "",

    taxClearanceFile: null,
    taxClearanceFileName: "",

    agmDocumentFile: null,
    agmDocumentFileName: "",

    otherDocumentFiles: [],
    otherDocumentFileNames: [],
    otherDocumentDocumentUrls: [],

    otherDocumentFile: null,
    otherDocumentFileName: "",
  });

  const [errors, setErrors] = useState<any>({});

  const scrollToFirstError = (validationErrors: Record<string, string>) => {
    const fieldOrder = [
      "registrationNumber",
      "businessregistrationNumber",
      "nameNepali",
      "nameEnglish",
      "natureOfWork",
      "status",
      "mergedCooperativeName",
      "mergedDateNepali",
      "mergedDateEnglish",
      "workingArea",
      "panNumber",
      "contactPerson",
      "mobileNumber",
      "email",
      "registeredDate",
      "registeredEnglishDate",
      "registeredAt",
      "registeredOfficeAddress",
      "maleMembers",
      "femaleMembers",
      "otherMembers",
      "staffCount",
      "softwareUsed",
      "isRegisteredInFederation",
      "closeDateNepali",
      "closeDateEnglish",
    ];

    const firstErrorField = fieldOrder.find((field) => validationErrors[field]);

    if (!firstErrorField) return;

    const element =
      (document.querySelector(
        `[data-field="${firstErrorField}"]`,
      ) as HTMLElement | null) ||
      (document.querySelector(
        `[name="${firstErrorField}"]`,
      ) as HTMLElement | null);

    if (element) {
      element.scrollIntoView({ behavior: "smooth", block: "center" });
    }
  };

  const validateContactFields = () => {
    let errors: any = {};

    if (currentStep === 0) {
      const cooperativeErrors = getCooperativeInfoErrors(cooperativeInfo);
      if (Object.keys(cooperativeErrors).length > 0) {
        errors.cooperativeInfo = cooperativeErrors;
      }
    }

    if (currentStep === 1) {
      const addressErrors = getAddressErrors(address);
      if (Object.keys(addressErrors).length > 0) {
        errors.address = addressErrors;
      }
    }

    if (currentStep === 2) {
      const shareErrors = getShareFinancialErrors(shareFinancial);
      if (Object.keys(shareErrors).length > 0) {
        setFinancialsActiveTab(getShareFinancialTab(shareErrors));
        errors.shareFinancial = shareErrors;
      }

      const loanErrors = getLoanFinancialErrors(loanFinancial);
      if (Object.keys(loanErrors).length > 0) {
        setFinancialsActiveTab(getLoanFinancialTab(loanErrors));
        errors.loanFinancial = loanErrors;
      }

      const incomeErrors = getIncomeFinancialErrors(incomeFinancial);
      if (Object.keys(incomeErrors).length > 0) {
        setFinancialsActiveTab(getIncomeFinancialTab(incomeErrors));
        errors.incomeFinancial = incomeErrors;
      }

      const auditErrors = getAuditFinancialErrors(auditFinancial);
      if (Object.keys(auditErrors).length > 0) {
        setFinancialsActiveTab(getAuditFinancialTab(auditErrors));
        errors.auditFinancial = auditErrors;
      }

      const taxErrors = getTaxFinancialErrors(taxFinancial);
      if (Object.keys(taxErrors).length > 0) {
        setFinancialsActiveTab(getTaxFinancialTab(taxErrors));
        errors.taxFinancial = taxErrors;
      }
    }

    if (currentStep === 3) {
      if (committeeTableCooperative.length === 0) {
        setCommitteeActiveTab("committees");

        errors.committeeTableCooperative =
          "At least one committee member is required.";
      } else if (executiveTableCooperative.length === 0) {
        setCommitteeActiveTab("executive");

        errors.executiveTableCooperative =
          "At least one executive member is required.";
      }
    }

    if (currentStep === 4) {
      const shareMemberErrors = getShareMemberErrors(shareMember);

      if (Object.keys(shareMemberErrors).length > 0) {
        errors.shareMember = shareMemberErrors;
      }
    }

    if (currentStep === 5) {
      const documentErrors = getDocumentErrors(documents);

      if (Object.keys(documentErrors).length > 0) {
        errors.documents = documentErrors;
      }
    }

    if (currentStep === 6) {
      const declarationErrors = getDeclarationErrors(declaration);

      if (Object.keys(declarationErrors).length > 0) {
        errors.declaration = declarationErrors;
      }
    }

    return errors;
  };

  const validateStep = () => {
    let newErrors: any = {};

    const contactErrors = validateContactFields();

    newErrors = {
      ...newErrors,
      ...contactErrors,
    };

    setErrors(newErrors);

    if (newErrors.cooperativeInfo) {
      scrollToFirstError(newErrors.cooperativeInfo);
    }

    return Object.keys(newErrors).length === 0;
  };

  // Validate a specific step by index (does NOT depend on currentStep state)
  const validateStepByIndex = (stepIndex: number): any => {
    const errs: any = {};

    if (stepIndex === 0) {
      const cooperativeErrors = getCooperativeInfoErrors(cooperativeInfo);
      if (Object.keys(cooperativeErrors).length > 0)
        errs.cooperativeInfo = cooperativeErrors;
    }

    if (stepIndex === 1) {
      const addressErrors = getAddressErrors(address);
      if (Object.keys(addressErrors).length > 0) errs.address = addressErrors;
    }

    if (stepIndex === 2) {
      const shareErrors = getShareFinancialErrors(shareFinancial);
      if (Object.keys(shareErrors).length > 0)
        errs.shareFinancial = shareErrors;

      const loanErrors = getLoanFinancialErrors(loanFinancial);
      if (Object.keys(loanErrors).length > 0) errs.loanFinancial = loanErrors;

      const incomeErrors = getIncomeFinancialErrors(incomeFinancial);
      if (Object.keys(incomeErrors).length > 0)
        errs.incomeFinancial = incomeErrors;

      const auditErrors = getAuditFinancialErrors(auditFinancial);
      if (Object.keys(auditErrors).length > 0)
        errs.auditFinancial = auditErrors;

      const taxErrors = getTaxFinancialErrors(taxFinancial);
      if (Object.keys(taxErrors).length > 0) errs.taxFinancial = taxErrors;
    }

    if (stepIndex === 3) {
      if (committeeTableCooperative.length === 0)
        errs.committeeTableCooperative =
          "At least one committee member is required.";
      else if (executiveTableCooperative.length === 0)
        errs.executiveTableCooperative =
          "At least one executive member is required.";
    }

    if (stepIndex === 4) {
      const shareMemberErrors = getShareMemberErrors(shareMember);
      if (Object.keys(shareMemberErrors).length > 0)
        errs.shareMember = shareMemberErrors;
    }

    if (stepIndex === 5) {
      const documentErrors = getDocumentErrors(documents);
      if (Object.keys(documentErrors).length > 0)
        errs.documents = documentErrors;
    }

    if (stepIndex === 6) {
      const declarationErrors = getDeclarationErrors(declaration);
      if (Object.keys(declarationErrors).length > 0)
        errs.declaration = declarationErrors;
    }

    return errs;
  };

  // Click a step in the bar: validate every step from 0 up to targetIndex-1.
  // Stop at the first failing step — jump to it and show its errors.
  const handleStepClick = async (targetIndex: number) => {
    if (targetIndex === currentStep) return;

    // Clicking backward is always allowed (save current data first)
    if (targetIndex < currentStep) {
      await saveToLocalStorage(
        currentStep,
        cooperativeInfo,
        address,
        shareFinancial,
        loanFinancial,
        incomeFinancial,
        auditFinancial,
        taxFinancial,
        committeeCooperative,
        executiveCooperative,
        committeeTableCooperative,
        executiveTableCooperative,
        shareMember,
        declaration,
        documents,
      );
      setErrors({});
      setCurrentStep(targetIndex);
      setFinancialsActiveTab("share");
      setCommitteeActiveTab("committees");
      scrollToStepper();
      return;
    }

    // Clicking forward: every step before targetIndex must pass validation
    for (let i = 0; i < targetIndex; i++) {
      const stepErrors = validateStepByIndex(i);
      if (Object.keys(stepErrors).length > 0) {
        // Jump to the failing step and show errors
        setErrors(stepErrors);
        setCurrentStep(i);
        if (i === 2) {
          // highlight the first broken financial tab
          if (stepErrors.shareFinancial)
            setFinancialsActiveTab(
              getShareFinancialTab(stepErrors.shareFinancial),
            );
          else if (stepErrors.loanFinancial)
            setFinancialsActiveTab(
              getLoanFinancialTab(stepErrors.loanFinancial),
            );
          else if (stepErrors.incomeFinancial)
            setFinancialsActiveTab(
              getIncomeFinancialTab(stepErrors.incomeFinancial),
            );
          else if (stepErrors.auditFinancial)
            setFinancialsActiveTab(
              getAuditFinancialTab(stepErrors.auditFinancial),
            );
          else if (stepErrors.taxFinancial)
            setFinancialsActiveTab(getTaxFinancialTab(stepErrors.taxFinancial));
        }
        if (i === 3) {
          if (stepErrors.committeeTableCooperative)
            setCommitteeActiveTab("committees");
          else if (stepErrors.executiveTableCooperative)
            setCommitteeActiveTab("executive");
        }
        if (stepErrors.cooperativeInfo)
          scrollToFirstError(stepErrors.cooperativeInfo);
        scrollToStepper();
        return;
      }
    }

    // All prior steps valid — jump to target
    await saveToLocalStorage(
      currentStep,
      cooperativeInfo,
      address,
      shareFinancial,
      loanFinancial,
      incomeFinancial,
      auditFinancial,
      taxFinancial,
      committeeCooperative,
      executiveCooperative,
      committeeTableCooperative,
      executiveTableCooperative,
      shareMember,
      declaration,
      documents,
    );
    setErrors({});
    setCurrentStep(targetIndex);
    setFinancialsActiveTab("share");
    setCommitteeActiveTab("committees");
    scrollToStepper();
  };

  const financialstabs = [
    { key: "share", label: "Share" },
    { key: "loan", label: "Capital and Loan" },
    { key: "income", label: "Income and Expense" },
    { key: "audit", label: "Audit" },
    { key: "tax", label: "Tax" },
  ];

  const committeetabs = [
    { key: "committees", label: "Committees" },
    { key: "executive", label: "Executive" },
  ];
  const [currentStep, setCurrentStep] = useState(0);
  const stepperRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLDivElement>(null);
  const arrowRefs = useRef<Array<HTMLDivElement | null>>([]);
  const [progressWidth, setProgressWidth] = useState("0%");
  const isMobile = useMobile();

  const isInitialLoadDone = useRef(false);

  useEffect(() => {
    const initData = async () => {
      try {
        await loadAllRegisterData(
          setCooperativeInfo,
          setAddress,
          setShareFinancial,
          setLoanFinancial,
          setIncomeFinancial,
          setAuditFinancial,
          setTaxFinancial,
          setCommitteeCooperative,
          setExecutiveCooperative,
          setCommitteeTableCooperative,
          setExecutiveTableCooperative,
          setShareMember,
          setDeclaration,
          setDocuments,
        );
      } finally {
        setTimeout(() => {
          isInitialLoadDone.current = true;
        }, 500);
      }
    };
    initData();
  }, []);

  // Auto-save drafts on field change (typing, select dropdown, date pick, image/file upload)
  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveCooperativeDraft(cooperativeInfo);
    }, 300);
    return () => clearTimeout(timer);
  }, [cooperativeInfo]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveAddressDraft(address);
    }, 300);
    return () => clearTimeout(timer);
  }, [address]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveShareFinancialDraft(shareFinancial);
    }, 300);
    return () => clearTimeout(timer);
  }, [shareFinancial]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveLoanFinancialDraft(loanFinancial);
    }, 300);
    return () => clearTimeout(timer);
  }, [loanFinancial]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveIncomeFinancialDraft(incomeFinancial);
    }, 300);
    return () => clearTimeout(timer);
  }, [incomeFinancial]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveAuditFinancialDraft(auditFinancial);
    }, 300);
    return () => clearTimeout(timer);
  }, [auditFinancial]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveTaxFinancialDraft(taxFinancial);
    }, 300);
    return () => clearTimeout(timer);
  }, [taxFinancial]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveCommitteeCooperativeDraft(committeeCooperative);
    }, 300);
    return () => clearTimeout(timer);
  }, [committeeCooperative]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveExecutiveCooperativeDraft(executiveCooperative);
    }, 300);
    return () => clearTimeout(timer);
  }, [executiveCooperative]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveCommitteeTableDraft(committeeTableCooperative);
    }, 300);
    return () => clearTimeout(timer);
  }, [committeeTableCooperative]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveExecutiveTableDraft(executiveTableCooperative);
    }, 300);
    return () => clearTimeout(timer);
  }, [executiveTableCooperative]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveShareMemberDraft(shareMember);
    }, 300);
    return () => clearTimeout(timer);
  }, [shareMember]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveDocumentsDraft(documents);
    }, 300);
    return () => clearTimeout(timer);
  }, [documents]);

  useEffect(() => {
    if (!isInitialLoadDone.current) return;
    const timer = setTimeout(() => {
      saveDeclarationDraft(declaration);
    }, 300);
    return () => clearTimeout(timer);
  }, [declaration]);

  const saveCurrentFormData = (step = currentStep) => {
    saveToLocalStorage(
      step,
      cooperativeInfo,
      address,
      shareFinancial,
      loanFinancial,
      incomeFinancial,
      auditFinancial,
      taxFinancial,
      committeeCooperative,
      executiveCooperative,
      committeeTableCooperative,
      executiveTableCooperative,
      shareMember,
      declaration,
      documents,
    );
  };

  const next = () => {
    // If on Step 2 (Financials): navigate through financial tabs
    if (currentStep === 2) {
      const financialTabKeys = ["share", "loan", "income", "audit", "tax"];
      const currentIndex = financialTabKeys.indexOf(financialsactiveTab);

      if (financialsactiveTab === "share") {
        const shareErrors = getShareFinancialErrors(shareFinancial);
        if (Object.keys(shareErrors).length > 0) {
          setErrors({ shareFinancial: shareErrors });
          scrollToStepper();
          return;
        }
      } else if (financialsactiveTab === "loan") {
        const loanErrors = getLoanFinancialErrors(loanFinancial);
        if (Object.keys(loanErrors).length > 0) {
          setErrors({ loanFinancial: loanErrors });
          scrollToStepper();
          return;
        }
      } else if (financialsactiveTab === "income") {
        const incomeErrors = getIncomeFinancialErrors(incomeFinancial);
        if (Object.keys(incomeErrors).length > 0) {
          setErrors({ incomeFinancial: incomeErrors });
          scrollToStepper();
          return;
        }
      } else if (financialsactiveTab === "audit") {
        const auditErrors = getAuditFinancialErrors(auditFinancial);
        if (Object.keys(auditErrors).length > 0) {
          setErrors({ auditFinancial: auditErrors });
          scrollToStepper();
          return;
        }
      } else if (financialsactiveTab === "tax") {
        const taxErrors = getTaxFinancialErrors(taxFinancial);
        if (Object.keys(taxErrors).length > 0) {
          setErrors({ taxFinancial: taxErrors });
          scrollToStepper();
          return;
        }
        const step2Errors = validateStepByIndex(2);
        if (Object.keys(step2Errors).length > 0) {
          setErrors(step2Errors);
          if (step2Errors.shareFinancial) setFinancialsActiveTab("share");
          else if (step2Errors.loanFinancial) setFinancialsActiveTab("loan");
          else if (step2Errors.incomeFinancial)
            setFinancialsActiveTab("income");
          else if (step2Errors.auditFinancial) setFinancialsActiveTab("audit");
          else if (step2Errors.taxFinancial) setFinancialsActiveTab("tax");
          scrollToStepper();
          return;
        }
      }

      if (currentIndex >= 0 && currentIndex < financialTabKeys.length - 1) {
        saveCurrentFormData();
        setErrors({});
        setFinancialsActiveTab(financialTabKeys[currentIndex + 1]);
        scrollToStepper();
        return;
      } else {
        // Last tab of Step 2 -> advance to Step 3
        saveCurrentFormData();
        setErrors({});
        setCurrentStep((prev) => prev + 1);
        setCommitteeActiveTab("committees");
        scrollToStepper();
        return;
      }
    }

    // If on Step 3 (Committee & Executive): navigate through committee tabs
    if (currentStep === 3) {
      const committeeTabKeys = ["committees", "executive"];
      const currentIndex = committeeTabKeys.indexOf(committeeactiveTab);

      if (committeeactiveTab === "committees") {
        if (committeeTableCooperative.length === 0) {
          setErrors({
            committeeTableCooperative:
              "At least one committee member is required.",
          });
          scrollToStepper();
          return;
        }
      } else if (committeeactiveTab === "executive") {
        if (executiveTableCooperative.length === 0) {
          setErrors({
            executiveTableCooperative:
              "At least one executive member is required.",
          });
          scrollToStepper();
          return;
        }
        if (committeeTableCooperative.length === 0) {
          setCommitteeActiveTab("committees");
          setErrors({
            committeeTableCooperative:
              "At least one committee member is required.",
          });
          scrollToStepper();
          return;
        }
      }

      if (currentIndex >= 0 && currentIndex < committeeTabKeys.length - 1) {
        saveCurrentFormData();
        setErrors({});
        setCommitteeActiveTab(committeeTabKeys[currentIndex + 1]);
        scrollToStepper();
        return;
      } else {
        // Last tab of Step 3 -> advance to Step 4
        saveCurrentFormData();
        setErrors({});
        setCurrentStep((prev) => prev + 1);
        scrollToStepper();
        return;
      }
    }

    // Default step navigation for steps without tabs
    if (currentStep < steps.length - 1) {
      const isValid = validateStep();
      if (isValid) {
        saveCurrentFormData();
        setErrors({});
        setCurrentStep((prev) => prev + 1);
        if (currentStep + 1 === 2) {
          setFinancialsActiveTab("share");
        } else if (currentStep + 1 === 3) {
          setCommitteeActiveTab("committees");
        }
        scrollToStepper();
      }
    }
  };

  const prev = () => {
    // If on Step 2 (Financials): navigate back through financial tabs or to Step 1
    if (currentStep === 2) {
      const financialTabKeys = ["share", "loan", "income", "audit", "tax"];
      const currentIndex = financialTabKeys.indexOf(financialsactiveTab);

      if (currentIndex > 0) {
        saveCurrentFormData();
        setErrors({});
        setFinancialsActiveTab(financialTabKeys[currentIndex - 1]);
        scrollToStepper();
        return;
      } else {
        saveCurrentFormData();
        setErrors({});
        setCurrentStep(1);
        scrollToStepper();
        return;
      }
    }

    // If on Step 3 (Committee & Executive): navigate back through committee tabs or to Step 2
    if (currentStep === 3) {
      const committeeTabKeys = ["committees", "executive"];
      const currentIndex = committeeTabKeys.indexOf(committeeactiveTab);

      if (currentIndex > 0) {
        saveCurrentFormData();
        setErrors({});
        setCommitteeActiveTab(committeeTabKeys[currentIndex - 1]);
        scrollToStepper();
        return;
      } else {
        saveCurrentFormData();
        setErrors({});
        setCurrentStep(2);
        setFinancialsActiveTab("tax");
        scrollToStepper();
        return;
      }
    }

    // If on Step 4 (Share Member): previous goes to Step 3's last tab (executive)
    if (currentStep === 4) {
      saveCurrentFormData();
      setErrors({});
      setCurrentStep(3);
      setCommitteeActiveTab("executive");
      scrollToStepper();
      return;
    }

    // Default step navigation for steps without tabs
    if (currentStep > 0) {
      saveCurrentFormData();
      setErrors({});
      setCurrentStep((prev) => prev - 1);
      scrollToStepper();
    }
  };

  const scrollToStepper = () => {
    setTimeout(() => {
      if (stepperRef.current) {
        stepperRef.current.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      }
    }, 200);
  };

  const progress = ((currentStep + 1) / steps.length) * 100;

  useEffect(() => {
    const updateProgressWidth = () => {
      if (!progressRef.current) return;

      if (currentStep >= steps.length - 1) {
        setProgressWidth("100%");
        return;
      }

      const targetArrow = arrowRefs.current[currentStep];
      if (!targetArrow) {
        setProgressWidth(`${progress}%`);
        return;
      }

      const containerRect = progressRef.current.getBoundingClientRect();
      const arrowRect = targetArrow.getBoundingClientRect();
      const width = Math.min(
        containerRect.width,
        Math.max(0, arrowRect.right - containerRect.left),
      );

      setProgressWidth(`${width}px`);
    };

    updateProgressWidth();
    const handleResize = () => updateProgressWidth();
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [currentStep, steps.length, progress]);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("registrationNumber", cooperativeInfo.registrationNumber);
      formData.append(
        "businessregistrationNumber",
        cooperativeInfo.businessregistrationNumber,
      );
      formData.append("nameNepali", cooperativeInfo.nameNepali);
      formData.append("nameEnglish", cooperativeInfo.nameEnglish);
      formData.append("natureOfWork", cooperativeInfo.natureOfWork);
      formData.append("status", cooperativeInfo.status);
      formData.append(
        "mergedCooperativeName",
        cooperativeInfo.mergedCooperativeName,
      );
      formData.append("mergedDateNepali", cooperativeInfo.mergedDateNepali);
      formData.append("mergedDateEnglish", cooperativeInfo.mergedDateEnglish);
      formData.append("mergeReason", cooperativeInfo.mergeReason);
      formData.append("closeDateNepali", cooperativeInfo.closeDateNepali);
      formData.append("closeDateEnglish", cooperativeInfo.closeDateEnglish);
      formData.append("panNumber", cooperativeInfo.panNumber);
      formData.append("email", cooperativeInfo.email);
      formData.append("mobileNumber", cooperativeInfo.mobileNumber);
      formData.append("contactPerson", cooperativeInfo.contactPerson);
      formData.append("registeredDate", cooperativeInfo.registeredDate);
      formData.append(
        "registeredEnglishDate",
        cooperativeInfo.registeredEnglishDate,
      );
      formData.append("workingArea", cooperativeInfo.workingArea.join(","));
      formData.append("registeredAt", cooperativeInfo.registeredAt);
      formData.append(
        "registeredOfficeAddress",
        cooperativeInfo.registeredOfficeAddress,
      );
      formData.append(
        "isRegisteredInFederation",
        String(cooperativeInfo.isRegisteredInFederation),
      );
      formData.append("womenLead", String(cooperativeInfo.womenLead));
      formData.append("staffCount", String(cooperativeInfo.staffCount));
      formData.append("softwareUsed", cooperativeInfo.softwareUsed);
      formData.append("maleMembers", String(cooperativeInfo.maleMembers));
      formData.append("femaleMembers", String(cooperativeInfo.femaleMembers));
      formData.append("otherMembers", String(cooperativeInfo.otherMembers));

      formData.append("address[province]", address.province);
      formData.append("address[district]", address.district);
      formData.append("address[municipality]", address.municipality);
      formData.append("address[streetTole]", address.streetTole);
      formData.append("address[ward]", String(address.ward));

      Object.entries(shareFinancial).forEach(([key, value]) => {
        formData.append(`financial[share][${key}]`, String(value ?? ""));
      });

      Object.entries(loanFinancial).forEach(([key, value]) => {
        formData.append(`financial[loan][${key}]`, String(value ?? ""));
      });

      Object.entries(incomeFinancial).forEach(([key, value]) => {
        formData.append(`financial[income][${key}]`, String(value ?? ""));
      });

      Object.entries(auditFinancial).forEach(([key, value]) => {
        if (key !== "file" && key !== "fileName") {
          formData.append(`financial[audit][${key}]`, String(value ?? ""));
        }
      });
      if (auditFinancial.file) {
        formData.append("auditFile", auditFinancial.file);
      }

      Object.entries(taxFinancial).forEach(([key, value]) => {
        formData.append(`financial[tax][${key}]`, String(value ?? ""));
      });

      committeeTableCooperative.forEach((member, index) => {
        Object.entries(member).forEach(([key, value]) => {
          if (
            key !== "file" &&
            key !== "fileName" &&
            key !== "bodFile" &&
            key !== "bodFileName" &&
            key !== "citizenshipFront" &&
            key !== "citizenshipFrontName" &&
            key !== "citizenshipFrontUrl" &&
            key !== "citizenshipBack" &&
            key !== "citizenshipBackName" &&
            key !== "citizenshipBackUrl"
          ) {
            formData.append(
              `cooperativeandexecutive[committeemember][${index}][${key}]`,
              String(value ?? ""),
            );
          }
        });

        if (member.file) {
          formData.append("committeePhoto", member.file);
        }

        if (member.bodFile) {
          formData.append("bodDocument", member.bodFile);
        }
        if (member.citizenshipFront) {
          formData.append("committeeCitizenshipFront", member.citizenshipFront);
        }
        if (member.citizenshipBack) {
          formData.append("committeeCitizenshipBack", member.citizenshipBack);
        }
      });

      executiveTableCooperative.forEach((member, index) => {
        Object.entries(member).forEach(([key, value]) => {
          if (
            key !== "file" &&
            key !== "fileName" &&
            key !== "documentUrl" &&
            key !== "citizenshipFront" &&
            key !== "citizenshipFrontName" &&
            key !== "citizenshipFrontUrl" &&
            key !== "citizenshipBack" &&
            key !== "citizenshipBackName" &&
            key !== "citizenshipBackUrl"
          ) {
            formData.append(
              `cooperativeandexecutive[executivemember][${index}][${key}]`,
              String(value ?? ""),
            );
          }
        });

        if (member.file) {
          formData.append("executivePhoto", member.file);
        }
        if (member.citizenshipFront) {
          formData.append("executiveCitizenshipFront", member.citizenshipFront);
        }
        if (member.citizenshipBack) {
          formData.append("executiveCitizenshipBack", member.citizenshipBack);
        }
      });

      Object.entries(shareMember).forEach(([key, value]) => {
        if (
          key !== "shareMemberFile" &&
          key !== "savingMemberFile" &&
          key !== "loanMemberFile"
        ) {
          formData.append(`shareMember[${key}]`, String(value ?? ""));
        }
      });

      if (shareMember.shareMemberFile) {
        formData.append("shareMemberFile", shareMember.shareMemberFile);
      }

      if (shareMember.savingMemberFile) {
        formData.append("savingMemberFile", shareMember.savingMemberFile);
      }

      if (shareMember.loanMemberFile) {
        formData.append("loanMemberFile", shareMember.loanMemberFile);
      }

      formData.append("declaration[name]", declaration.name);
      formData.append("declaration[position]", declaration.position);
      formData.append("declaration[mobile]", declaration.mobile);
      formData.append(
        "declaration[declaration]",
        String(declaration.declaration),
      );

      if (documents.regCertificateFile) {
        formData.append("regCertificateUrl", documents.regCertificateFile);
      }
      if (documents.lawDocumentFile) {
        formData.append("lawDocumentUrl", documents.lawDocumentFile);
      }
      if (documents.foundingDocumentFile) {
        formData.append("foundingDocumentUrl", documents.foundingDocumentFile);
      }
      if (documents.meetingDocumentFile) {
        formData.append("meetingDocumentUrl", documents.meetingDocumentFile);
      }
      if (documents.agmDocumentFile) {
        formData.append("agmDocumentUrl", documents.agmDocumentFile);
      }
      // if (documents.auditDocumentFile) {
      //   formData.append("auditDocumentUrl", documents.auditDocumentFile);
      // }

      const otherFiles =
        documents.otherDocumentFiles && documents.otherDocumentFiles.length > 0
          ? documents.otherDocumentFiles
          : documents.otherDocumentFile
            ? [documents.otherDocumentFile]
            : [];
      otherFiles.forEach((file) => {
        formData.append("otherDocumentUrl", file);
      });

      if (documents.panDocumentFile) {
        formData.append("panDocumentUrl", documents.panDocumentFile);
      }

      if (documents.businessRegistrationFile) {
        formData.append(
          "businessRegistrationUrl",
          documents.businessRegistrationFile,
        );
      }

      if (documents.taxClearanceFile) {
        formData.append("taxClearanceUrl", documents.taxClearanceFile);
      }

      const response = await axiosClient.post(`/registers`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200 || response.status === 201) {
        clearRegisterLocalStorage();
        sendNotification(
          `Cooperative registered`,
          user?.user?.userName || "User",
        );
        dynamicSuccess("Cooperative registered successfully!");
        navigate("/cooperatives-list");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
      setLoading(false);
    }
  };

  const handleDraftSubmit = async () => {
    const registrationErrors: Record<string, string> = {};

    if (!cooperativeInfo.registrationNumber.trim()) {
      registrationErrors.registrationNumber =
        "Registration Number is required.";
    }

    if (Object.keys(registrationErrors).length > 0) {
      setErrors({
        cooperativeInfo: registrationErrors,
      });

      setCurrentStep(0);
      scrollToStepper();

      setTimeout(() => {
        scrollToFirstError(registrationErrors);
      }, 200);

      return;
    }
    setLoading(true);
    try {
      const formData = new FormData();

      formData.append("registrationNumber", cooperativeInfo.registrationNumber);
      formData.append(
        "businessregistrationNumber",
        cooperativeInfo.businessregistrationNumber,
      );
      formData.append("nameNepali", cooperativeInfo.nameNepali);
      formData.append("nameEnglish", cooperativeInfo.nameEnglish);
      formData.append("natureOfWork", cooperativeInfo.natureOfWork);
      formData.append(
        "mergedCooperativeName",
        cooperativeInfo.mergedCooperativeName,
      );
      formData.append("mergedDateNepali", cooperativeInfo.mergedDateNepali);
      formData.append("mergedDateEnglish", cooperativeInfo.mergedDateEnglish);
      formData.append("mergeReason", cooperativeInfo.mergeReason);
      formData.append("closeDateNepali", cooperativeInfo.closeDateNepali);
      formData.append("closeDateEnglish", cooperativeInfo.closeDateEnglish);
      formData.append("panNumber", cooperativeInfo.panNumber);
      formData.append("email", cooperativeInfo.email);
      formData.append("mobileNumber", cooperativeInfo.mobileNumber);
      formData.append("contactPerson", cooperativeInfo.contactPerson);
      formData.append("registeredDate", cooperativeInfo.registeredDate);
      formData.append(
        "registeredEnglishDate",
        cooperativeInfo.registeredEnglishDate,
      );
      formData.append("workingArea", cooperativeInfo.workingArea.join(","));
      formData.append("registeredAt", cooperativeInfo.registeredAt);
      formData.append(
        "registeredOfficeAddress",
        cooperativeInfo.registeredOfficeAddress,
      );
      formData.append(
        "isRegisteredInFederation",
        String(cooperativeInfo.isRegisteredInFederation),
      );
      formData.append("womenLead", String(cooperativeInfo.womenLead));
      formData.append("staffCount", String(cooperativeInfo.staffCount));
      formData.append("softwareUsed", cooperativeInfo.softwareUsed);
      formData.append("maleMembers", String(cooperativeInfo.maleMembers));
      formData.append("femaleMembers", String(cooperativeInfo.femaleMembers));
      formData.append("otherMembers", String(cooperativeInfo.otherMembers));
      formData.append("status", "Draft");
      formData.append("address[province]", address.province);
      formData.append("address[district]", address.district);
      formData.append("address[municipality]", address.municipality);
      formData.append("address[streetTole]", address.streetTole);
      formData.append("address[ward]", String(address.ward));

      Object.entries(shareFinancial).forEach(([key, value]) => {
        formData.append(`financial[share][${key}]`, String(value ?? ""));
      });

      Object.entries(loanFinancial).forEach(([key, value]) => {
        formData.append(`financial[loan][${key}]`, String(value ?? ""));
      });

      Object.entries(incomeFinancial).forEach(([key, value]) => {
        formData.append(`financial[income][${key}]`, String(value ?? ""));
      });

      Object.entries(auditFinancial).forEach(([key, value]) => {
        if (key !== "file" && key !== "fileName") {
          formData.append(`financial[audit][${key}]`, String(value ?? ""));
        }
      });
      if (auditFinancial.file) {
        formData.append("auditFile", auditFinancial.file);
      }

      Object.entries(taxFinancial).forEach(([key, value]) => {
        formData.append(`financial[tax][${key}]`, String(value ?? ""));
      });

      committeeTableCooperative.forEach((member, index) => {
        Object.entries(member).forEach(([key, value]) => {
          if (
            key !== "file" &&
            key !== "fileName" &&
            key !== "bodFile" &&
            key !== "bodFileName" &&
            key !== "citizenshipFront" &&
            key !== "citizenshipFrontName" &&
            key !== "citizenshipFrontUrl" &&
            key !== "citizenshipBack" &&
            key !== "citizenshipBackName" &&
            key !== "citizenshipBackUrl"
          ) {
            formData.append(
              `cooperativeandexecutive[committeemember][${index}][${key}]`,
              String(value ?? ""),
            );
          }
        });

        if (member.file) {
          formData.append("committeePhoto", member.file);
        }

        if (member.bodFile) {
          formData.append("bodDocument", member.bodFile);
        }
        if (member.citizenshipFront) {
          formData.append("committeeCitizenshipFront", member.citizenshipFront);
        }
        if (member.citizenshipBack) {
          formData.append("committeeCitizenshipBack", member.citizenshipBack);
        }
      });

      executiveTableCooperative.forEach((member, index) => {
        Object.entries(member).forEach(([key, value]) => {
          if (key !== "file" && key !== "fileName") {
            formData.append(
              `cooperativeandexecutive[executivemember][${index}][${key}]`,
              String(value ?? ""),
            );
          }
        });

        if (member.file) {
          formData.append("executivePhoto", member.file);
        }
        if (member.citizenshipFront) {
          formData.append("executiveCitizenshipFront", member.citizenshipFront);
        }
        if (member.citizenshipBack) {
          formData.append("executiveCitizenshipBack", member.citizenshipBack);
        }
      });

      Object.entries(shareMember).forEach(([key, value]) => {
        if (
          key !== "shareMemberFile" &&
          key !== "savingMemberFile" &&
          key !== "loanMemberFile"
        ) {
          formData.append(`shareMember[${key}]`, String(value ?? ""));
        }
      });

      if (shareMember.shareMemberFile) {
        formData.append("shareMemberFile", shareMember.shareMemberFile);
      }

      if (shareMember.savingMemberFile) {
        formData.append("savingMemberFile", shareMember.savingMemberFile);
      }

      if (shareMember.loanMemberFile) {
        formData.append("loanMemberFile", shareMember.loanMemberFile);
      }

      formData.append("declaration[name]", declaration.name);
      formData.append("declaration[position]", declaration.position);
      formData.append("declaration[mobile]", declaration.mobile);
      formData.append(
        "declaration[declaration]",
        String(declaration.declaration),
      );

      if (documents.regCertificateFile) {
        formData.append("regCertificateUrl", documents.regCertificateFile);
      }
      if (documents.lawDocumentFile) {
        formData.append("lawDocumentUrl", documents.lawDocumentFile);
      }
      if (documents.foundingDocumentFile) {
        formData.append("foundingDocumentUrl", documents.foundingDocumentFile);
      }
      if (documents.meetingDocumentFile) {
        formData.append("meetingDocumentUrl", documents.meetingDocumentFile);
      }
      if (documents.agmDocumentFile) {
        formData.append("agmDocumentUrl", documents.agmDocumentFile);
      }
      // if (documents.auditDocumentFile) {
      //   formData.append("auditDocumentUrl", documents.auditDocumentFile);
      // }

      const otherDraftFiles =
        documents.otherDocumentFiles && documents.otherDocumentFiles.length > 0
          ? documents.otherDocumentFiles
          : documents.otherDocumentFile
            ? [documents.otherDocumentFile]
            : [];
      otherDraftFiles.forEach((file) => {
        formData.append("otherDocumentUrl", file);
      });

      if (documents.panDocumentFile) {
        formData.append("panDocumentUrl", documents.panDocumentFile);
      }

      if (documents.businessRegistrationFile) {
        formData.append(
          "businessRegistrationUrl",
          documents.businessRegistrationFile,
        );
      }

      if (documents.taxClearanceFile) {
        formData.append("taxClearanceUrl", documents.taxClearanceFile);
      }

      const response = await axiosClient.post(`/registers/draft`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response.status === 200 || response.status === 201) {
        clearRegisterLocalStorage();
        sendNotification(
          `Cooperative draft registered`,
          user?.user?.userName || "User",
        );
        dynamicSuccess("Draft Registered successfully!");
        navigate("/cooperatives-list");
      }
    } catch (error: any) {
      const responseMessage = error?.response?.data?.message;

      const message = Array.isArray(responseMessage)
        ? responseMessage[0]
        : responseMessage || "Something went wrong";

      dynamicFailed(message);
      setLoading(false);
    }
  };

  const isObjectFilled = (obj: any) => {
    if (!obj) return false;

    return Object.entries(obj).every(([key, value]) => {
      if (
        ignoredKeys.includes(key) ||
        key.endsWith("File") ||
        key.endsWith("FileName") ||
        key.endsWith("DocumentUrl") ||
        key.endsWith("Files") ||
        key.endsWith("FileNames") ||
        key.endsWith("DocumentUrls")
      ) {
        return true;
      }

      // Validate merged fields only when status is Merged
      const mergedFields = [
        "mergedCooperativeName",
        "mergedDateNepali",
        "mergedDateEnglish",
        "mergeReason",
      ];

      if (
        mergedFields.includes(key) &&
        cooperativeInfo.status?.trim().toLowerCase() !== "merged"
      ) {
        return true;
      }

      // Validate closed fields only when status is Deactivate
      const closedFields = ["closeDateNepali", "closeDateEnglish"];

      if (
        closedFields.includes(key) &&
        cooperativeInfo.status?.trim().toLowerCase() !== "deactivate"
      ) {
        return true;
      }

      return value !== null && value !== "" && value !== undefined;
    });
  };

  const isTableFilled = (table: any[]) => {
    if (!table || table.length === 0) return false;

    return table.every((item) =>
      Object.entries(item).every(([key, value]) => {
        if (key === "husbandName" && item.maritalStatus === "Single") {
          return true;
        }

        if (
          ignoredKeys.includes(key) ||
          key.endsWith("File") ||
          key.endsWith("FileName") ||
          key.endsWith("DocumentUrl") ||
          key.endsWith("Files") ||
          key.endsWith("FileNames") ||
          key.endsWith("DocumentUrls")
        ) {
          return true;
        }

        return value !== null && value !== "" && value !== undefined;
      }),
    );
  };

  const isFormComplete = () => {
    return (
      isObjectFilled(cooperativeInfo) &&
      isObjectFilled(address) &&
      isObjectFilled(shareFinancial) &&
      isObjectFilled(loanFinancial) &&
      isObjectFilled(incomeFinancial) &&
      isObjectFilled(auditFinancial) &&
      isTableFilled(committeeTableCooperative) &&
      isTableFilled(executiveTableCooperative) &&
      isObjectFilled(shareMember) &&
      isObjectFilled(documents) &&
      isObjectFilled(declaration) &&
      declaration.declaration === "true"
    );
  };

  const disabled = !isFormComplete();
  return (
    <>
      <div
        className="d-flex justify-content-between align-items-center mb-3"
        ref={stepperRef}
      >
        <div>
          <h3>Cooperatives Registration Form</h3>
          <small>
            Please fill out all the required information in each step
          </small>
        </div>
      </div>
      <div className="row g-2">
        <div className="col-md-12">
          <div className="dashboard-card">
            <div className="card-body">
              <div className="stepper-container">
                <div className="stepper d-flex align-items-center justify-content-between flex-nowrap overflow-auto text-nowrap">
                  {steps.map((step, index) => {
                    const Icon = step.icon;
                    return (
                      <React.Fragment key={index}>
                        <div
                          className={`step ${
                            index <= currentStep ? "active" : ""
                          } ${index !== currentStep ? "step-clickable" : ""}`}
                          onClick={() => handleStepClick(index)}
                          title={step.label}
                        >
                          {index < currentStep ? (
                            <i className="me-1">
                              <CircleCheck
                                size={20}
                                className="me-2 text-success"
                              />
                            </i>
                          ) : (
                            index === currentStep && (
                              <i className="me-1">
                                <Icon size={20} />
                              </i>
                            )
                          )}
                          <small
                            className={`step-label ${index === currentStep ? "active" : ""}`}
                          >
                            {step.label}
                          </small>
                        </div>
                        {index !== steps.length - 1 && (
                          <div
                            ref={(el) => {
                              arrowRefs.current[index] = el;
                            }}
                            className={`step-arrow mx-1 ${index <= currentStep ? "active" : ""}`}
                          >
                            <i>
                              <ChevronRight />
                            </i>
                          </div>
                        )}

                        {index !== steps.length - 1 && (
                          <div
                            className={`step-line ${index < currentStep ? "active" : ""}`}
                          ></div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
                <div className="progress mt-3" ref={progressRef}>
                  <div
                    className="progress-bar"
                    style={{ width: progressWidth }}
                  ></div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="d-flex align-items-center my-3">
        {currentStep === 0 && (
          <>
            <div>
              <i className={`primary-color me-2`}>
                <List size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Cooperative Info</h5>
              <small>
                Please fill out all the required information in each step
              </small>
            </div>
          </>
        )}
        {currentStep === 1 && (
          <>
            <div>
              <i className={`primary-color me-2`}>
                <MapPin size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Cooperative Address</h5>
              <small>Please tell us your cooperative’s address details</small>
            </div>
          </>
        )}
        {currentStep === 2 && (
          <>
            <div className={`d-flex flex-column gap-3`}>
              <div className="d-flex align-items-center">
                <div>
                  <i className={`primary-color me-2`}>
                    <Banknote size={30} />
                  </i>
                </div>
                <div>
                  <h5 className="m-0">Financial Info</h5>
                  <small>Provide financial details of the cooperative</small>
                </div>
              </div>

              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">
                    Fiscal Year <span className="primary-color">*</span>
                  </label>

                  <SelectWithError
                    styles={customSelectStyles}
                    isError={Boolean(errors.shareFinancial?.fiscalYear)}
                    options={fiscalYearOptions}
                    placeholder="Select the Fiscal Year"
                    value={
                      fiscalYearOptions.find(
                        (option) => option.value === shareFinancial.fiscalYear,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) => {
                      // Synchronize the fiscal year across all financial tabs
                      setShareFinancial((prev: any) => ({
                        ...prev,
                        fiscalYear: selected?.value,
                      }));

                      setLoanFinancial((prev: any) => ({
                        ...prev,
                        fiscalYear: selected?.value,
                      }));

                      setIncomeFinancial((prev: any) => ({
                        ...prev,
                        fiscalYear: selected?.value,
                      }));

                      setAuditFinancial((prev: any) => ({
                        ...prev,
                        fiscalYear: selected?.value,
                      }));

                      setTaxFinancial((prev: any) => ({
                        ...prev,
                        fiscalYear: selected?.value,
                      }));
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />

                  {errors.shareFinancial?.fiscalYear && (
                    <p className="error-message">
                      {errors.shareFinancial.fiscalYear}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {currentStep === 3 && (
          <>
            <div className={`d-flex flex-column gap-3`}>
              <div className="d-flex align-items-center">
                <div>
                  <i className={`primary-color me-2`}>
                    <UserRound size={30} />
                  </i>
                </div>
                <div>
                  <h5 className="m-0">Committees & Executive</h5>
                  <small>
                    Enter the Committee and Executive details of the
                    cooperative.
                  </small>
                </div>
              </div>
              <div className="row g-3">
                <div className="col-12">
                  <label className="form-label">
                    Fiscal Year <span className="primary-color">*</span>
                  </label>

                  <SelectWithError
                    styles={customSelectStyles}
                    isError={Boolean(errors.committeeCooperative?.fiscalYear)}
                    options={fiscalYearOptions}
                    placeholder="Select the Fiscal Year"
                    value={
                      fiscalYearOptions.find(
                        (option) =>
                          option.value === committeeCooperative.fiscalYear,
                      ) || null
                    }
                    onChange={(selected: SelectOption | null) => {
                      setCommitteeCooperative((prev: any) => ({
                        ...prev,
                        fiscalYear: selected?.value,
                      }));

                      setExecutiveCooperative((prev: any) => ({
                        ...prev,
                        fiscalYear: selected?.value,
                      }));
                    }}
                    menuPortalTarget={document.body}
                    menuPosition="fixed"
                  />

                  {errors.committeeCooperative?.fiscalYear && (
                    <p className="error-message">
                      {errors.committeeCooperative.fiscalYear}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
        {currentStep === 4 && (
          <>
            <div>
              <i className={`primary-color me-2`}>
                <User size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Share member</h5>
              <small>Upload cooperative member.</small>
            </div>
          </>
        )}
        {currentStep === 5 && (
          <>
            <div>
              <i className={`primary-color me-2`}>
                <FileText size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Documents</h5>
              <small>Upload the required documents for this section.</small>
            </div>
          </>
        )}
        {currentStep === 6 && (
          <>
            <div>
              <i className={`primary-color me-2`}>
                <ShieldCheck size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Declaration</h5>
              <small>Details of the Person Submitting the Report</small>
            </div>
          </>
        )}
        {currentStep === 7 && (
          <>
            <div>
              <i className={`primary-color me-2`}>
                <ScanEye size={30} />
              </i>
            </div>
            <div>
              <h5 className="m-0">Review All Info</h5>
              <small>Please review draft before submisison.</small>
            </div>
          </>
        )}
      </div>

      {/* Tabs */}
      {currentStep === 2 && (
        <ul className="nav nav-tabs mt-2 flex-nowrap overflow-auto text-nowrap">
          {financialstabs.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                className={`nav-link ${financialsactiveTab === tab.key ? "active" : ""}`}
                onClick={() => {
                  setFinancialsActiveTab(tab.key);
                  saveToLocalStorage(
                    currentStep,
                    cooperativeInfo,
                    address,
                    shareFinancial,
                    loanFinancial,
                    incomeFinancial,
                    auditFinancial,
                    taxFinancial,
                    committeeCooperative,
                    executiveCooperative,
                    committeeTableCooperative,
                    executiveTableCooperative,
                    shareMember,
                    declaration,
                    documents,
                  );
                }}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {currentStep === 3 && (
        <ul className="nav nav-tabs mt-2">
          {committeetabs.map((tab) => (
            <li className="nav-item" key={tab.key}>
              <button
                className={`nav-link ${committeeactiveTab === tab.key ? "active" : ""}`}
                onClick={() => {
                  setCommitteeActiveTab(tab.key);
                  saveToLocalStorage(
                    currentStep,
                    cooperativeInfo,
                    address,
                    shareFinancial,
                    loanFinancial,
                    incomeFinancial,
                    auditFinancial,
                    taxFinancial,
                    committeeCooperative,
                    executiveCooperative,
                    committeeTableCooperative,
                    executiveTableCooperative,
                    shareMember,
                    declaration,
                    documents,
                  );
                }}
              >
                {tab.label}
              </button>
            </li>
          ))}
        </ul>
      )}

      {/* --------------- */}

      {currentStep === 0 && (
        <CooperativeInfo
          currentStep={currentStep}
          cooperativeInfo={cooperativeInfo}
          setCooperativeInfo={setCooperativeInfo}
          errors={errors.cooperativeInfo || {}}
        />
      )}

      {currentStep === 1 && (
        <Address
          currentStep={currentStep}
          address={address}
          setAddress={setAddress}
          errors={errors.address || {}}
        />
      )}

      {currentStep === 2 && (
        <Financials
          currentStep={currentStep}
          financialsactiveTab={financialsactiveTab}
          shareFinancial={shareFinancial}
          setShareFinancial={setShareFinancial}
          loanFinancial={loanFinancial}
          setLoanFinancial={setLoanFinancial}
          incomeFinancial={incomeFinancial}
          setIncomeFinancial={setIncomeFinancial}
          auditFinancial={auditFinancial}
          setAuditFinancial={setAuditFinancial}
          taxFinancial={taxFinancial}
          setTaxFinancial={setTaxFinancial}
          errorsShare={errors.shareFinancial || {}}
          errorsLoan={errors.loanFinancial || {}}
          errorsIncome={errors.incomeFinancial || {}}
          errorsAudit={errors.auditFinancial || {}}
          errorsTax={errors.taxFinancial || {}}
        />
      )}

      {currentStep === 3 && (
        <Committee
          committeeactiveTab={committeeactiveTab}
          committeeCooperative={committeeCooperative}
          setCommitteeCooperative={setCommitteeCooperative}
          executiveCooperative={executiveCooperative}
          setExecutiveCooperative={setExecutiveCooperative}
          errorsCommittee={errors.committeeTableCooperative}
          errorsExecutive={errors.executiveTableCooperative}
          committeeTableCooperative={committeeTableCooperative}
          setCommitteeTableCooperative={setCommitteeTableCooperative}
          editCommitteeIndex={editCommitteeIndex}
          setEditCommitteeIndex={setEditCommitteeIndex}
          executiveTableCooperative={executiveTableCooperative}
          setExecutiveTableCooperative={setExecutiveTableCooperative}
          editExecutiveIndex={editExecutiveIndex}
          setEditExecutiveIndex={setEditExecutiveIndex}
          setErrors={setErrors}
        />
      )}

      {currentStep === 4 && (
        <ShareMember
          shareMember={shareMember}
          setShareMember={setShareMember}
          errors={errors.shareMember || {}}
        />
      )}

      {currentStep === 5 && (
        <Documents
          documents={documents}
          setDocuments={setDocuments}
          errors={errors.documents || {}}
        />
      )}

      {currentStep === 6 && (
        <Declaration
          declaration={declaration}
          setDeclaration={setDeclaration}
          errors={errors.declaration || {}}
        />
      )}

      {currentStep === 7 && (
        <Review
          cooperativeInfo={cooperativeInfo}
          address={address}
          shareFinancial={shareFinancial}
          loanFinancial={loanFinancial}
          incomeFinancial={incomeFinancial}
          auditFinancial={auditFinancial}
          committeeCooperative={committeeTableCooperative}
          executiveCooperative={executiveTableCooperative}
          shareMember={shareMember}
          documents={documents}
          declaration={declaration}
          setCurrentStep={setCurrentStep}
          scrollToStepper={scrollToStepper}
        />
      )}

      <div
        className={`d-flex mt-4 ${currentStep === 0 ? "justify-content-end" : "justify-content-between"} ${isMobile ? "flex-wrap gap-2" : ""}`}
      >
        {currentStep !== 0 && (
          <button className="btn px-3 btn-badge-border" onClick={prev}>
            Previous
          </button>
        )}

        <div className="d-flex gap-2">
          <button
            className="btn btn-badge-border px-3"
            onClick={handleDraftSubmit}
          >
            Save Draft
          </button>
          {currentStep === steps.length - 1 ? (
            <span
              title={disabled ? "Please fill all required fields" : ""}
              style={{ display: "inline-block" }}
            >
              <button
                className="btn btn-badge-primary px-3"
                onClick={handleSubmit}
                disabled={disabled}
                style={{ pointerEvents: disabled ? "none" : "auto" }}
              >
                Submit
              </button>
            </span>
          ) : (
            <button className="btn btn-badge-primary px-3" onClick={next}>
              Next Step
            </button>
          )}
        </div>
      </div>
    </>
  );
};

export default RegisterNew;
